// PHASE-2.01 START
'use client';
import * as React from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

interface PopoverProps {
  trigger: React.ReactNode;
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  asChild?: boolean;
  align?: 'left' | 'right';
  usePortal?: boolean;
}

export function Popover({ trigger, children, open: controlledOpen, onOpenChange, asChild = false, align = 'right', usePortal = false }: PopoverProps) {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const [mounted, setMounted] = React.useState(false);
  const [position, setPosition] = React.useState({ top: 0, left: 0 });
  const ref = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLDivElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const isControlled = typeof controlledOpen === 'boolean';
  const open = isControlled ? Boolean(controlledOpen) : internalOpen;

  React.useEffect(() => {
    setMounted(true);
  }, []);

  const setOpen = React.useCallback(
    (next: boolean) => {
      if (!isControlled) {
        setInternalOpen(next);
      }
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  // Update position when portal is used and popover opens
  React.useEffect(() => {
    if (!open || !usePortal || !triggerRef.current) return;

    const updatePosition = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      const contentWidth = contentRef.current?.offsetWidth ?? 192;
      let left = align === 'left' ? rect.left : rect.right - contentWidth;
      left = Math.max(8, Math.min(left, window.innerWidth - contentWidth - 8));
      setPosition({ top: rect.bottom + 8, left });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, usePortal, align]);

  React.useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!triggerRef.current?.contains(e.target as Node) &&
          !contentRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown, { capture: true });
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown, { capture: true } as any);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, setOpen]);

  const triggerNode = React.useMemo(() => {
    if (asChild && React.isValidElement(trigger)) {
      return React.cloneElement(trigger, {
        onClick: (event: React.MouseEvent) => {
          if (typeof trigger.props?.onClick === 'function') {
            trigger.props.onClick(event);
          }
          setOpen(!open);
        },
        'aria-expanded': open,
      });
    }
    return (
      <button
        type="button"
        className="h-11 px-3 rounded-lg border border-border/70 bg-background/70 hover:bg-background"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        {trigger}
      </button>
    );
  }, [asChild, trigger, open, setOpen]);

  const content = open && (
    <div
      ref={contentRef}
      role="menu"
      className={cn(
        'z-[9999] min-w-48 rounded-xl p-1 border border-border/60 shadow-xl backdrop-blur-sm',
        usePortal ? 'fixed bg-background/95 dark:bg-background/85' : 'absolute mt-2 bg-background/95 dark:bg-background/85',
        !usePortal && (align === 'left' ? 'left-0' : 'right-0')
      )}
      style={usePortal ? {
        top: `${position.top}px`,
        left: `${position.left}px`,
        backdropFilter: 'blur(24px)',
        backgroundColor: 'hsl(var(--background) / 0.95)'
      } : undefined}
    >
      {children}
    </div>
  );

  return (
    <div className="relative inline-block" ref={usePortal ? triggerRef : ref}>
      {triggerNode}
      {!usePortal && content}
      {usePortal && mounted && createPortal(content, document.body)}
    </div>
  );
}
// PHASE-2.01 END
